import { Box, IconButton, Stack, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "../assets/SVGs/Login/VisibilityIcon";
import { WeeklyMeetsAPI } from "../api/weekly-meets";
import { filterArrayByString } from "../componets/helper";
import AppLayout from "../componets/AppLayout/AppLayout";
import { DataGrid } from "@mui/x-data-grid";
import GridCustomHeader from "../componets/AppLayout/GridCustomHeader";
import AddWeeklyMeetModal from "../componets/Modal/AddWeeklyMeetModal";
import { defaultEditFormState } from "../componets/constant";
import WeeklyMeetEditModal from "../componets/Modal/WeeklyMeetEditModal";
import dayjs from "dayjs";
import { useAppContext } from "../context/AppContext";
import { DatePicker } from "@mui/x-date-pickers";

const WeeklyMeets = () => {
  const { openSnack } = useAppContext();
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  const [openEditModal, setOpenEditModal] = useState(defaultEditFormState);

  const columns = [
    {
      field: "weekly_meet_date",
      headerName: "Weekly Meet Date",
      flex: 1,
      editable: true,
      type: "date",
      valueGetter: (params) => {
        return dayjs(params).toDate(); // returns a native JS Date
      },
      renderCell: (params) => {
        return params.value ? dayjs(params.value).format("MM/DD/YYYY") : "";
      },
      renderEditCell: (params) => {
        const handleChange = (event) => {
          const newValue = event;
          params.api.setEditCellValue({
            id: params.id,
            field: "weekly_meet_date",
            value: newValue,
          });
        };

        return (
          <DatePicker
            value={dayjs(params.value)}
            onChange={handleChange}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "weekly_meet_date",
                name: "weekly_meet_date",
                className: "login-textField",
                size: "small",
              },
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="view"
          size="small"
          onClick={() =>
            setOpenEditModal({
              open: true,
              id: params?.row?.id,
              title: params?.row?.weekly_meet_date,
            })
          }
        >
          <VisibilityIcon
            sx={{
              color: "#64748B",
            }}
          />
        </IconButton>
      ),
    },
  ];

  const handleSearch = (searchString) => {
    setSearchText(searchString);
  };

  const fetch = async () => {
    setIsLoading(true);
    try {
      setData(await WeeklyMeetsAPI.getMeets());
    } catch (e) {
      console.log(e.response);
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    isLoading && fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const getRowsData = () => {
    const dummyRows = [...(data?.items || [])];

    return filterArrayByString(dummyRows || [], searchText);
  };

  const processRowUpdate = async (newRow, oldRow) => {
    try {
      await editWeeklyMeet(newRow);
      return newRow; // Return the updated row
    } catch (error) {
      console.error("Row update failed:", error);
      return oldRow; // Revert to the old row if the update fails
    }
  };

  const editWeeklyMeet = async (form) => {
    const formObj = {
      weekly_meet_date: dayjs(form.weekly_meet_date).format("YYYY-MM-DD"),
    };
    try {
      await WeeklyMeetsAPI.updateMeet(formObj, form?.id);
      openSnack("Weekly Meet information updated successfully.", "success");
      fetch();
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  return (
    <AppLayout>
      <Box
        sx={{
          width: "100%",
          padding: isMobileTable ? "8px" : "24px",
        }}
      >
        {/* Custom Header */}
        <GridCustomHeader
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          searchText={searchText}
          handleSearch={handleSearch}
          setOpenModal={setOpenModal}
          buttonText="Add New Weekly Meet"
          headerName="Weekly Meets"
        />

        {/* Data Grid */}
        <Stack
          sx={{
            width: "100%",
            "& .MuiDataGrid-root": {
              borderRadius: "8px",
            },
          }}
          className="customDataGridFigma"
        >
          <DataGrid
            rows={getRowsData() || []}
            columns={columns}
            loading={isLoading}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
            }}
            pageSizeOptions={[10, 15, 20, 25]}
            checkboxSelection={false}
            rowSelection={false}
            rowHeight={48}
            columnHeaderHeight={40}
            getRowClassName={(params) => {
              const customClassName =
                params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
              return ` ${customClassName}`.trim();
            }}
            slotProps={{
              loadingOverlay: {
                variant: "linear-progress",
                noRowsVariant: "linear-progress",
              },
            }}
            localeText={{
              noRowsLabel: "No weekly meets found.",
            }}
            processRowUpdate={processRowUpdate}
          />
        </Stack>
        {openModal && (
          <AddWeeklyMeetModal
            open={openModal}
            handleClose={() => setOpenModal(false)}
            setIsLoading={setIsLoading}
          />
        )}

        {openEditModal?.open && (
          <WeeklyMeetEditModal
            open={openEditModal?.open}
            id={openEditModal?.id}
            handleClose={() => setOpenEditModal(defaultEditFormState)}
            setIsLoading={setIsLoading}
            title={dayjs(openEditModal?.title).format("MM/DD/YYYY")}
          />
        )}
      </Box>
    </AppLayout>
  );
};

export default WeeklyMeets;
